import type { Dispatch } from 'react';
import { useCallback, useMemo } from 'react';

import type { SearchParamsValuesType } from '../../../../constants';
import type { GlobalFilters } from '../../../../contexts/FilterContext';
import type { AdditionalFilterInDropdown, SelectedFiltersValues } from '../AdditionalFilters/constants';
import {
  isBooleanFilterValue,
  isCalendarDateFilterValue,
  isLineItemEndDateFilterValue,
  isRangeFilterFilled,
  isRangeFilterValue,
} from '../AdditionalFilters/utils';
import { getTransformedSelectedDropdownFilters } from './utils';

export type HookProps = {
  selectedDropdownFilters: AdditionalFilterInDropdown[];
  onSelectedDropdownFilters: Dispatch<React.SetStateAction<AdditionalFilterInDropdown[]>>;
  selectedFiltersValues: SelectedFiltersValues;
  applyCommonFilter: <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => void;
};

export type ReturnedHookType = {
  transformedSelectedDropdownFilters: AdditionalFilterInDropdown[];
  isFilterChipShown: boolean;
  onRemoveSingleFilter: (selectedSearchParam: SearchParamsValuesType) => void;
  onRemoveAllFilter: () => void;
};

export const useHandleDropdownFilters = ({
  selectedFiltersValues,
  selectedDropdownFilters,
  applyCommonFilter,
  onSelectedDropdownFilters,
}: HookProps): ReturnedHookType => {
  const transformedSelectedDropdownFilters: AdditionalFilterInDropdown[] = useMemo(
    () => getTransformedSelectedDropdownFilters(selectedDropdownFilters, selectedFiltersValues),
    [selectedDropdownFilters, selectedFiltersValues]
  );

  const isFilterChipShown = useMemo(() => !!transformedSelectedDropdownFilters.length, [
    transformedSelectedDropdownFilters,
  ]);

  const onRemoveSingleFilter = useCallback(
    (selectedSearchParam: SearchParamsValuesType): void => {
      const selectedValue = selectedFiltersValues[selectedSearchParam];

      if (isRangeFilterValue(selectedValue)) {
        if (isRangeFilterFilled(selectedValue)) applyCommonFilter(selectedSearchParam, null);
      } else if (
        isLineItemEndDateFilterValue(selectedValue) ||
        isCalendarDateFilterValue(selectedValue) ||
        isBooleanFilterValue(selectedValue)
      ) {
        applyCommonFilter(selectedSearchParam, null);
      } else if (Array.isArray(selectedValue) && selectedValue.length) {
        applyCommonFilter(selectedSearchParam, []);
      }

      onSelectedDropdownFilters((prevSelectedFiltersDropdowns) =>
        prevSelectedFiltersDropdowns.filter((filter) => filter.searchParam !== selectedSearchParam)
      );
    },
    [applyCommonFilter, selectedFiltersValues, onSelectedDropdownFilters]
  );

  const onRemoveAllFilter = useCallback(() => {
    const selectedFilters = transformedSelectedDropdownFilters.flatMap((filter) => filter.searchParam);

    if (selectedFilters.length) {
      selectedFilters.forEach((filter) => {
        applyCommonFilter(filter, null);
      });
    }

    onSelectedDropdownFilters([]);
  }, [transformedSelectedDropdownFilters, applyCommonFilter, onSelectedDropdownFilters]);

  return {
    transformedSelectedDropdownFilters,
    isFilterChipShown,
    onRemoveSingleFilter,
    onRemoveAllFilter,
  };
};
